import type {
  EngagementItemResponse,
  ThreadDetailResponseDto,
  ThreadListResponseDto,
  ThreadMarkReadResponseDto,
} from './billing';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const engagementApi = {
  getInbox: (
    workspaceId: string,
    params?: {
      status?: string;
      brand?: string;
      platform?: string;
      sentiment?: string;
      page?: number;
      size?: number;
    },
  ) =>
    api
      .get<EngagementItemResponse[]>(`/api/workspaces/${workspaceId}/engagement`, { params })
      .then((r) => r.data),
  getCounts: (workspaceId: string) =>
    api
      .get<{
        total: number;
        byBrand: Record<string, number>;
      }>(`/api/workspaces/${workspaceId}/engagement/counts`)
      .then((r) => r.data),
  sendReply: (workspaceId: string, itemId: string, content: string, aiSuggested: boolean) =>
    api
      .post(`/api/workspaces/${workspaceId}/engagement/${itemId}/reply`, { content, aiSuggested })
      .then((r) => r.data),
  /**
   * V112 — delete the brand-side reply tied to this item, both on the
   * platform AND locally. 409 when the item has no captured platform id
   * (pre-V112 reply or polled-in own reply with no external_item_id).
   */
  deleteReply: (workspaceId: string, itemId: string) =>
    api
      .delete<{ status: string }>(`/api/workspaces/${workspaceId}/engagement/${itemId}/reply`)
      .then((r) => r.data),
  updateStatus: (workspaceId: string, itemId: string, status: string) =>
    api
      .patch(`/api/workspaces/${workspaceId}/engagement/${itemId}/status`, { status })
      .then((r) => r.data),
  regenerateSuggestion: (workspaceId: string, itemId: string) =>
    api.post(`/api/workspaces/${workspaceId}/engagement/${itemId}/suggest`).then((r) => r.data),
  triggerPoll: (workspaceId: string) =>
    api.post(`/api/workspaces/${workspaceId}/engagement/poll`).then((r) => r.data),
  /** Comments + mentions tied to a specific post — drives the inline list on PostCard. */
  byPost: (workspaceId: string, postId: string, limit = 10) =>
    api
      .get<
        EngagementItemResponse[]
      >(`/api/workspaces/${workspaceId}/engagement/by-post/${postId}`, { params: { limit } })
      .then((r) => r.data),
  // ── Phase 12 — thread-grouped endpoints ─────────────────────────────
  listThreads: (
    workspaceId: string,
    params?: { filter?: string; brandId?: string; platform?: string; page?: number; size?: number },
  ) =>
    api
      .get<ThreadListResponseDto>(`/api/workspaces/${workspaceId}/engagement/threads`, { params })
      .then((r) => r.data),
  getThread: (workspaceId: string, threadKey: string) =>
    api
      .get<ThreadDetailResponseDto>(
        `/api/workspaces/${workspaceId}/engagement/threads/${encodeURIComponent(threadKey)}`,
      )
      .then((r) => r.data),
  markThreadRead: (workspaceId: string, threadKey: string) =>
    api
      .post<ThreadMarkReadResponseDto>(
        `/api/workspaces/${workspaceId}/engagement/threads/${encodeURIComponent(threadKey)}/read`,
      )
      .then((r) => r.data),
};

/**
 * Dispatched by the thread inbox after a successful mark-as-read so the
 * NotificationBell + Sidebar badges re-fetch immediately instead of
 * waiting for their 60s interval. Each badge attaches a one-line listener
 * that triggers its existing tick().
 */
export const ENGAGEMENT_COUNTS_INVALIDATE_EVENT = 'merkflow:engagement-counts-invalidate';
export function dispatchEngagementCountsInvalidate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ENGAGEMENT_COUNTS_INVALIDATE_EVENT));
  }
}

export const feedSyncApi = {
  triggerSync: (workspaceId: string) =>
    api.post<{ message: string }>(`/api/workspaces/${workspaceId}/sync`).then((r) => r.data),
  getSyncStatus: (workspaceId: string) =>
    api
      .get<{
        lastSyncedAt: string | null;
        connections: {
          connectionId: string;
          platform: string;
          lastSyncedAt: string | null;
          newPosts: number;
          status: string;
        }[];
      }>(`/api/workspaces/${workspaceId}/sync/status`)
      .then((r) => r.data),
  getBrandPosts: (
    workspaceId: string,
    brandId: string,
    params?: { source?: string; platform?: string; page?: number; size?: number },
  ) =>
    api
      .get<
        {
          id: string;
          brandId: string;
          workspaceId: string;
          platform: string;
          copy: string;
          hashtags: string[];
          status: string;
          publishedAt: string | null;
          visualUrl: string | null;
          reviewStatus: string | null;
          syncSource: string;
          externalPostId: string | null;
          contentType: string | null;
          createdAt: string | null;
        }[]
      >(`/api/workspaces/${workspaceId}/brands/${brandId}/posts`, { params })
      .then((r) => r.data),
};
